import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import './FacialExpressionPractice.scss';
import { API } from 'aws-amplify';
import { GetUserVideo, getVideo } from '../../s3Func/s3Download';

import tryAgain from '../Module1/assets/try_again.mp3';
import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat';
import MediaService from '../../services/MediaService';
import { DragDropContainer, DropTarget } from 'react-drag-drop-container';


const happyExample1 = 'appfiles/expressions/day-1-photo-smile.jpg';
const happyExample2 = 'appfiles/expressions/day-2-photo-smile.jpg';
const happyExample3 = 'appfiles/expressions/day-3-photo-smile.jpg';

const sadExample1 = 'appfiles/expressions/day-1-photo-sad.jpg';
const sadExample2 = 'appfiles/expressions/day-2-photo-sad.jpg';
const sadExample3 = 'appfiles/expressions/day-3-photo-sad.jpg';

/**
 *  Creates and runs the practice section for Unit1 Module3
 *  Note: This code is reused in other modules as well
 * @param {*} props
 * @returns the HTML code for the practice section.
 */
const FacialExpressionPractice = (props) => {
  // Initialize variables to be used later
  const background = props.background;
  const dispatch = useDispatch();
  const [practice, setPractice] = useState(false);
  const [practiceCount, setPracticeCount] = useState(0);
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);
  const [intro, setIntro] = useState(null);
  const [firstTime, setFirstTime] = useState(true);
  const [score, setScore] = useState(0);

  const happyArray = useMemo(
    () => [happyExample1, happyExample2, happyExample3],
    []
  );
  const sadArray = useMemo(() => [sadExample1, sadExample2, sadExample3], []);

  const [lizardAnimation, setLizardAnimation] = useState('');

  const [videoIntro, setVideoIntro] = useState('');
  const [displayVideoIntro, setDisplayVideoIntro] = useState(false);

  // Audio refs and sources
  const tryAgainAudioRef = useRef(null);
  const faceAudioRef = useRef(null);
  const awesomeJobAudioRef = useRef(null);
  const [faceAudioSrc, setFaceAudioSrc] = useState('');
  const [awesomeJobSrc, setAwesomeJobSrc] = useState('');
  const expectedFaceSrcRef = useRef('');
  const expectedAwesomeSrcRef = useRef('');

  const [showAnimation, setShowAnimation] = useState(false);
  const [showChallenge, setShowChallenge] = useState(false);
  const [selectedPhoto, setSelectedPhone] = useState('');
  const [answer, setAnswer] = useState('');
  const [wrongDrag, setWrongDrag] = useState(false);
  const wrongDragRef = useRef(false);

  const PRACTICE_MEDIA = {
    images: {},
    gifs: {},
    sound: {},
  };

  PRACTICE_MEDIA.images.smilingHead = MediaService.getImage(
    '/Module3Images/HeadWithEyes.png',
    login
  );

  PRACTICE_MEDIA.images.sadHead = MediaService.getImage(
    '/Module3Images/LizardSadFace.png',
    login
  );

  PRACTICE_MEDIA.images.lizardStill = MediaService.getImage(
    '/Module3Images/Lizard+closing+still+image.gif',
    login
  );

  PRACTICE_MEDIA.images.exit = MediaService.getImage(
    '/Images/close.png',
    login
  );

  // Randomly decide whether the image will be a happy one or a sad one.
  const  createImages = async () => {
    const { floor, random } = Math;

    // Generate a random number between 0 and 3
    const randomIndex = floor(random() * 3);

    // Generate a random number between 0 and 2
    const randomNum = floor(random() * 2);

    const images = randomNum === 1 ? happyArray[randomIndex] : sadArray[randomIndex];
    const image = await getVideo(images, login);
    const isHappy = image.includes('smile')
    setAnswer(isHappy ? 'happy' : 'sad');
    setSelectedPhone(image);
  }

  const handleCount = async () =>{
    if (practiceCount !== 0)
      {
        if (practiceCount < 3) {
          (async () => {
            await createImages()
          })();
          PRACTICE_MEDIA.sound.practiceIntro = await getVideo(
            'appfiles/expressions/practice_intro.mp3', login
          );
          setIntro(PRACTICE_MEDIA.sound.practiceIntro);
          setShowChallenge(true);
          setShowAnimation(false);
        }
        else{
          setShowChallenge(false);
          PRACTICE_MEDIA.gifs.lizardTalking = await getVideo(
            'appfiles/expressions/Lizard Awesome Job.gif', login
          );
          setLizardAnimation(PRACTICE_MEDIA.gifs.lizardTalking);
          PRACTICE_MEDIA.sound.awesomeJob = await getVideo(
            'appfiles/expressions/practice_finished.mp3', login
          );
          // Trigger awesome job audio via ref (onLoadedData)
          expectedAwesomeSrcRef.current = PRACTICE_MEDIA.sound.awesomeJob;
          setAwesomeJobSrc(PRACTICE_MEDIA.sound.awesomeJob);
        }
      }
  }


  useEffect(() => {
    handleCount();
  }, [practiceCount]);

  const start = async () =>{
    let videoUrl = '';
    const isModule8 = props.stepComplete === 'MODULE8_SET_stepCompleted';
    if (!props.unit4) {
      if (props.module.currentday === 1) {
        videoUrl = isModule8
          ? MediaService.getVideo('/Module8/practice_day_1.mp4', login)
          : MediaService.getVideo('/Module3Videos/practice_day_1.mp4', login);
      }
      else if (props.module.currentday === 2) {
        videoUrl = isModule8
          ? MediaService.getVideo('/Module8/practice_day_2.mp4', login)
          : MediaService.getVideo('/Module3Videos/practice_day_2.mp4', login);
      }
      else if (props.module.currentday === 3) {
        videoUrl = isModule8
          ? MediaService.getVideo('/Module8/practice_day_3.mp4', login)
          : MediaService.getVideo('/Module3Videos/practice_day_3.mp4', login);
      }
      else {
        videoUrl = await GetUserVideo(`influencers/${login.influencerid}/expressions_video_message.mp4`);
      }
      await createImages()
      setVideoIntro(videoUrl);
      setDisplayVideoIntro(true);
    }
    else{
      await createImages()
      handleIntroVideoEnded()
    }
  }

  useEffect(() => {
    start();
  }, []);

  const onHit = async (e) => {
    setWrongDrag(false);
    wrongDragRef.current = false;
    setIntro(null);
    handleDropPhoto(answer, selectedPhoto)
  }

  // Check the position of the image when we stop dragging it.
  const handleDropPhoto = async (type,photo) => {
    const isHappy = photo.includes('smile') && type === 'happy';
    const isSad = photo.includes('sad') && type === 'sad';

    PRACTICE_MEDIA.gifs.lizardHappyGif = await getVideo(
      'appfiles/expressions/Lizard Happy.gif', login
    );

    PRACTICE_MEDIA.gifs.lizardSadGif = await getVideo(
      'appfiles/expressions/Lizard Sad.gif', login
    );

    PRACTICE_MEDIA.sound.happyAnswer = await getVideo(
      'appfiles/expressions/practice_happy_correct.mp3', login
    );

    PRACTICE_MEDIA.sound.sadAnswer = await getVideo(
      'appfiles/expressions/practice_sad_correct.mp3', login
    );

    const audio = isHappy ? 'happyAnswer' : 'sadAnswer';
    const animation = isHappy ? 'lizardHappyGif' : 'lizardSadGif';

    if (isHappy || isSad) {
      if (firstTime) {
        setScore(score + 1);
      }
      // Stop try-again sound if it is playing
      if (tryAgainAudioRef.current && !tryAgainAudioRef.current.paused) {
        tryAgainAudioRef.current.pause();
        tryAgainAudioRef.current.currentTime = 0;
      }
      // Play correct face audio via ref, allowing repeat if same source
      const desiredSrc = PRACTICE_MEDIA.sound[audio];
      const faceEl = faceAudioRef.current;
      const sameSrc = faceAudioSrc === desiredSrc;
      if (faceEl && sameSrc) {
        try {
          faceEl.pause();
          faceEl.currentTime = 0;
          await faceEl.play();
        } catch (e) {
          const msg = String(e && e.message ? e.message : e);
          if (!msg.includes('interrupted by a new load request')) {
            console.log('face audio replay failed', e);
          }
        }
      } else {
        if (faceEl) {
          try { faceEl.pause(); } catch {}
        }
        expectedFaceSrcRef.current = desiredSrc;
        setFaceAudioSrc(desiredSrc);
      }
      setLizardAnimation(PRACTICE_MEDIA.gifs[animation]);
      setShowChallenge(false);
      setShowAnimation(true);
      setFirstTime(true);
    } else {
      // Replay try-again reliably on iOS
      const el = tryAgainAudioRef.current;
      if (el) {
        try {
          el.pause();
          el.currentTime = 0;
          await el.play();
        } catch (error) {
          console.log('Try again audio play failed:', error);
        }
      }
      setFirstTime(false);
    }
  };

  const handlePracticeEnded = async () => {
    setPracticeCount(practiceCount + 1);
  };

  const updateDB = async () => {
    try {
      await API.post('TIR', '/v3/student/result', {
        body: {
          student_id: login.studentid,
          metric: 'practice_expressions',
          result: JSON.stringify({
            day: module.currentday,
            created: new Date().toISOString(),
            review: props.world2 ? 1 : 0,
            values: [{ value: score, target: 3 }],
          }),
        },
      });
    } catch (error) {
      console.log(error);
    }
  }

  const handleAwesomeJobEnd = async () => {
    setShowAnimation(false);
    await updateDB();
    if (props.world2) {
      if(!props.module.reviewIcon) {
        dispatch({ type: props.stepComplete, payload: 'practice' });
      }
      props.setDisplayPractice(false);
    } 
    else if (props.unit4){
      props.setDisplayPractice(false);
      props.next()
    }
    else {
      if(!props.module.reviewIcon) {
        dispatch({ type: props.stepComplete, payload: 'practice' });
      }
      props.onPracticeClose(false);
      props.module.sectionCompleted = props.module.sectionCompleted + 1
    }
  };

  const handleIntroVideoEnded = async () => {
    if (props.stepComplete === 'MODULE8_SET_stepCompleted') {
      PRACTICE_MEDIA.sound.practiceIntro = await getVideo(
        'appfiles/expressions/review_practice_intro.mp3', login
      );
    } else {
      PRACTICE_MEDIA.sound.practiceIntro = await getVideo(
        'appfiles/expressions/practice_intro.mp3', login
      );
    }
    setDisplayVideoIntro(false);
    setPractice(true);
    setShowChallenge(true);
    setIntro(PRACTICE_MEDIA.sound.practiceIntro);
  };

  // The HTML code for practice.
  return (
    <>
      {displayVideoIntro && (
        <CloudfrontVideoFormat
          source={videoIntro}
          back='/facial-expression'
          close={() => {
            if (props.world2) {
              props.setDisplayPractice(false);
            } else {
              props.onPracticeClose(false);
              props.module.sectionCompleted = props.module.sectionCompleted + 1
            }
          }}
          background={background}
          ended={handleIntroVideoEnded}
        />
      )}

      {practice && (
        <div
          className='facial-expression-practice'
          style={{ backgroundImage: `url(${props.background})` }}
        >
          <header className='facial-expression-practice__header'>
            <h2>Drag the photo to the correct happy or sad face</h2>
            <button>
              <img
                src={PRACTICE_MEDIA.images.exit}
                alt=''
                onClick={() => {
                  if (props.world2 || props.unit4) {
                    props.setDisplayPractice(false);
                  } else {
                    props.onPracticeClose(false);
                    props.module.sectionCompleted = props.module.sectionCompleted + 1
                  }
                }}
              />
            </button>
          </header>

          {/* Always-mounted audio elements for reliable iOS playback */}
          <audio
            ref={tryAgainAudioRef}
            src={tryAgain}
            preload='auto'
            playsInline
          />
          <audio
            ref={faceAudioRef}
            src={faceAudioSrc}
            preload='auto'
            playsInline
            onLoadedData={() => {
              const el = faceAudioRef.current;
              if (!el) return;
              const expected = expectedFaceSrcRef.current;
              const currentSrc = el.currentSrc || el.src;
              if (!expected || (currentSrc && !currentSrc.includes(expected))) return;
              el.currentTime = 0;
              el.play().catch((e) => {
                const msg = String(e && e.message ? e.message : e);
                if (!msg.includes('interrupted by a new load request')) {
                  console.log('face audio play failed', e);
                }
              });
            }}
            onEnded={handlePracticeEnded}
          />
          <audio
            ref={awesomeJobAudioRef}
            src={awesomeJobSrc}
            preload='auto'
            playsInline
            onLoadedData={() => {
              const el = awesomeJobAudioRef.current;
              if (!el) return;
              const expected = expectedAwesomeSrcRef.current;
              const currentSrc = el.currentSrc || el.src;
              if (!expected || (currentSrc && !currentSrc.includes(expected))) return;
              el.currentTime = 0;
              el.play().catch((e) => {
                const msg = String(e && e.message ? e.message : e);
                if (!msg.includes('interrupted by a new load request')) {
                  console.log('awesome job audio play failed', e);
                }
              });
            }}
            onEnded={handleAwesomeJobEnd}
          />

          {showChallenge && (
            <main>
              <audio autoPlay>
                <source src={intro} type='audio/mp3' />
              </audio>
              <div className='drag-options'>
                <DropTarget targetKey={"happy"} onHit={(e)=>{onHit(e)}}>
                  <div className='drag-area-container'>
                    <img src={PRACTICE_MEDIA.images.smilingHead} />
                  </div>
                </DropTarget>
                <DragDropContainer 
                  targetKey={answer} 
                  onDragStart={() => {
                    setWrongDrag(true);
                    wrongDragRef.current = true;
                  }}
                  onDragEnd={async () => {
                    // Handle wrong drop - play try again audio
                    if (wrongDragRef.current) {
                      const el = tryAgainAudioRef.current;
                      if (el) {
                        try {
                          el.pause();
                          el.currentTime = 0;
                          await el.play();
                        } catch (error) {
                          console.log('Try again audio play failed:', error);
                        }
                      }
                      setFirstTime(false);
                    }
                  }}
                >
                  <img className='match-image' src={selectedPhoto} />
              </DragDropContainer>
                <DropTarget targetKey={"sad"} onHit={(e)=>{onHit(e)}}>
                  <div className='drag-area-container'>
                    <img src={PRACTICE_MEDIA.images.sadHead} />
                  </div>
                </DropTarget>
              </div>
            </main>
          )}
          {showAnimation && (
            <footer>
              <img className="lizard-character" src={lizardAnimation} />
              <img className="lizard-character" src={PRACTICE_MEDIA.images.lizardStill} />
            </footer>
          )}
        </div>
      )}
    </>
  );
};
export default FacialExpressionPractice;
